import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { VehicleType } from "@/types/car";

// Map category codes to VehicleType
const categoryToVehicleType: Record<string, VehicleType> = {
  'cars': 'car',
  'suv': 'car',
  'heavy_truck': 'truck',
  'light_truck': 'truck',
  'bus': 'truck',
  'motorcycle': 'motorcycle',
  'scooter': 'motorcycle',
  'atv': 'atv',
  'utv': 'atv',
  'van': 'van',
  'trailer': 'trailer',
  'agricultural': 'agricultural',
  'tractor': 'agricultural',
  'construction': 'construction',
  'boat': 'boat',
  'yacht': 'boat',
  'jet_ski': 'boat',
  'caravan': 'van',
  'camper': 'van',
};

interface CategoryCountResult {
  category_code: string;
  count: number;
}

export function useCategoryCounts(countryCode?: string | null) {
  return useQuery({
    queryKey: ['category-counts', countryCode],
    queryFn: async () => {
      // Use optimized RPC function - instant response
      const { data, error } = await (supabase.rpc as any)('get_category_counts', {
        p_country_code: countryCode || null
      });

      if (error) {
        console.error('Error fetching category counts:', error);
        throw error;
      }

      // Convert to VehicleType counts
      const vehicleCounts: Record<VehicleType, number> = {
        car: 0,
        truck: 0,
        motorcycle: 0,
        van: 0,
        trailer: 0,
        agricultural: 0,
        construction: 0,
        boat: 0,
        atv: 0,
      };

      const results = (data || []) as CategoryCountResult[];
      results.forEach((item) => {
        const vehicleType = categoryToVehicleType[item.category_code] || 'car';
        vehicleCounts[vehicleType] += Number(item.count);
      });

      // Calculate total
      const total = Object.values(vehicleCounts).reduce((sum, c) => sum + c, 0);

      return { counts: vehicleCounts, total };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes cache
    gcTime: 15 * 60 * 1000,
  });
}
